import 'core-js/stable'
import 'regenerator-runtime/runtime'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import mitt from 'mitt'
import vant from './plugins/vant'
import '@/assets/fonts/iconfont.css'
import { loading } from '@/utils/loading'
import { formatLangHtml, type FormatLangHtmlOptions } from '@/utils/format'
import i18n from './locales'
import { VueMasonryPlugin } from 'vue-masonry'
import track from './utils/track'
import VChart from 'vue-echarts'
import echarts from './utils/echarts'

// 在最开始就设置错误拦截，确保在 Bonree RUM 初始化之前
function shouldIgnoreError(errMsg: string, url?: string): boolean {
  return !!(
    errMsg.includes('nodeName') ||
    errMsg.includes('classList') ||
    errMsg.includes('cache_') ||
    errMsg.includes('SafeCheck') ||
    errMsg.includes('ChunkLoadError') ||
    errMsg.includes('Loading chunk') ||
    (url && url.includes('tcplayer'))
  )
}

// 保存原始的 console.error
const originalConsoleError = console.error
console.error = function (...args) {
  const errMsg = args.map(arg => {
    if (arg instanceof Error) return arg.message
    if (typeof arg === 'string') return arg
    return String(arg)
  }).join(' ')

  if (shouldIgnoreError(errMsg)) {
    return
  }
  originalConsoleError.apply(console, args)
}

track()

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const app = createApp(App)

app.component('v-chart', VChart)
app.provide('echarts', echarts)

app.use(vant).use(i18n).use(pinia).use(router).use(VueMasonryPlugin).mount('#app')

app.config.globalProperties.$loading = loading
app.config.globalProperties.$bus = mitt()
app.config.globalProperties.$formatLangHtml = (
  text: string,
  values: Array<string | number> = [],
  options?: FormatLangHtmlOptions
) => formatLangHtml(text, values, options)
app.config.globalProperties.$tVarHtml = (
  key: string,
  values: Array<string | number> = [],
  options?: FormatLangHtmlOptions
) => formatLangHtml(String(i18n.global.t(key)), values, options)
app.config.errorHandler = (err) => {
  if (err && typeof err === 'object') {
    const errMsg = 'message' in err ? String((err as { message?: unknown }).message) : ''
    const errName = 'name' in err ? String((err as { name?: unknown }).name) : ''
    if (!shouldIgnoreError(errMsg + ' ' + errName)) {
      // 处理错误，例如：报告给一个服务
    }
  }
}

// 全局兜底：确保 window.Wxsdk.wechatShare 在任何环境都存在空实现
if (!window.Wxsdk || typeof window.Wxsdk.wechatShare !== 'function') {
  window.Wxsdk = window.Wxsdk || {}
  window.Wxsdk.wechatShare = () => {}
}

// 全局捕获并忽略腾讯云播放器安全检查插件的错误
const originalOnError = window.onerror
window.onerror = function (msg, url, ...restArgs) {
  const errMsg = String(msg)
  if (shouldIgnoreError(errMsg, url)) {
    return true // 阻止错误继续传播
  }
  // 调用原始错误处理
  if (originalOnError) {
    return originalOnError.apply(this, [msg, url, ...restArgs])
  }
  return false
}

// 处理未捕获的 Promise rejection
const originalOnUnhandledRejection = window.onunhandledrejection
window.onunhandledrejection = function (event: PromiseRejectionEvent) {
  const errMsg = event.reason instanceof Error
    ? event.reason.message + ' ' + event.reason.name
    : String(event.reason)
  if (shouldIgnoreError(errMsg)) {
    event.preventDefault() // 阻止默认处理
    return true
  }
  if (originalOnUnhandledRejection) {
    return originalOnUnhandledRejection.apply(this, [event])
  }
  return false
}
